import React from "react";
import { AddOnsCalInterface } from "../../interface";
import { CustomSupply, Package } from "@/interfaces/Package";
import { type Drinker } from "../../../Quote/components/Drinker";
import {
    EstimatedTotals,
    EstimatedTotalsEvent,
    EstimatedTotalsInput,
    EstimatedTotalsOptions,
} from "../interface/priceCalculation.interface";
import { BEER_AND_WINE_COCKTAIL_RATIO } from "../constants/priceCalculation.constants";

export type {
    EstimatedTotals,
    EstimatedTotalsEvent,
    EstimatedTotalsInput,
    EstimatedTotalsOptions,
};

export const getNumberOfCocktails = (guests: number, numberOfHours: number, drinker?: Drinker) => {
    const Intguests = Math.ceil(guests);
    const IntHours = Math.ceil(numberOfHours);
    if (drinker === "heavy") return Intguests * IntHours * 2;
    if (drinker === "light") return Intguests * IntHours;
    return Intguests * (IntHours + 1);
};

export const getAddonPrice = (
    addon: AddOnsCalInterface,
    guests: number,
    bartenders: number,
    numberOfHours: number,
    bartenderPrepTime: number,
    drinker?: Drinker
): number => {
    switch (addon.type) {
        case "bartender":
            return Math.max(addon.price * bartenders, addon.min_price ?? 0);
        case "guests":
            return Math.max(addon.price * guests, addon.min_price ?? 0);
        case "hourly":
            if (addon.include_prep_time) {
                return Math.max(
                    addon.price *
                    (Number(numberOfHours) + Number(bartenderPrepTime) / 60),
                    addon.min_price ?? 0
                );
            } else {
                return Math.max(addon.price * numberOfHours, addon.min_price ?? 0);
            }
        case "NumOfCocktails":
            return Math.max(
                addon.price * getNumberOfCocktails(guests, numberOfHours, drinker),
                addon.min_price ?? 0
            );
        case "fixed":
        default:
            return Math.max(addon.price, addon.min_price ?? 0);
    }
};

const getAddonCost = (
    addon: AddOnsCalInterface,
    guests: number,
    bartenders: number,
    numberOfHours: number,
    bartenderPrepTime: number,
    drinker?: Drinker
): number => {
    switch (addon.type) {
        case "bartender":
            return addon.cost * bartenders;
        case "hourly":
            if (addon.include_prep_time) {
                return (
                    addon.cost * (Number(numberOfHours) + Number(bartenderPrepTime) / 60)
                );
            } else {
                return addon.cost * numberOfHours;
            }
        case "guests":
            return addon.cost * guests;

        case "NumOfCocktails":
            return addon.cost * getNumberOfCocktails(guests, numberOfHours, drinker);
        case "fixed":
        default:
            return addon.cost;
    }
};

export const getAddonsTotal = (
    addons: AddOnsCalInterface[],
    guests: number,
    bartenders: number,
    numberofhours: number,
    bartenderPrepTime: number,
    drinker?: Drinker
): number => {
    const totalAddonsPrice = addons.reduce((acc, addon) => {
        if (addon.auto_include) {
            acc += getAddonPrice(
                addon,
                guests,
                bartenders,
                numberofhours,
                bartenderPrepTime,
                drinker
            );
            return acc;
        } else {
            return acc;
        }
    }, 0);
    return totalAddonsPrice;
};
export const getCustomSupplyTotalCost = (
    customSupply: CustomSupply[],
    guests: number,
    bartenders: number,
    numberOfHours: number // Fixed casing
): number => {
    return customSupply?.reduce((acc, supply) => {
        return acc + supply.cost * getNumberOfCocktails(guests, numberOfHours);
    }, 0);
};
export const getCustomSupplyTotal = (
    customSupply: CustomSupply[],
    guests: number,
    bartenders: number,
    numberOfHours: number // Fixed casing
): number => {
    return customSupply?.reduce((acc, supply) => {
        return acc + supply.price * getNumberOfCocktails(guests, numberOfHours);
    }, 0);
};
export const getAddonsTotalCost = (
    addons: AddOnsCalInterface[],
    guests: number,
    bartenders: number,
    numberofhours: number,
    bartenderPrepTime: number,
    drinker?: Drinker
): number => {
    const totalAddonsPrice = addons.reduce((acc, addon) => {
        if (addon.auto_include) {
            acc += getAddonCost(
                addon,
                guests,
                bartenders,
                numberofhours,
                bartenderPrepTime,
                drinker
            );
            return acc;
        } else {
            return acc;
        }
    }, 0);
    return totalAddonsPrice;
};
export const getPlatformServiceFee = (platformFeeAmount: number, feeRate = 0.1) => {
    return platformFeeAmount - (platformFeeAmount / (1 + feeRate));
};
export const getNumberOfBartenders = (guests: number, guestsPerBartender: number) => {
    return Math.ceil(guests / guestsPerBartender);
};
export const getEstimatedDrinks = (guests: number, hours: number, drinker?: Drinker) => {
    const g = Number(guests);
    const h = Number(hours);
    if (drinker === "heavy") return h * 2 * g;
    if (drinker === "light") return h * g;
    return (h + 1) * g;
};

/**
 * Calculates Estimated Total Revenue, Estimated Cost/Expense and Estimated Profit (in dollars).
 * Mirrors the formulas used by the Price Calculator (PriceCalculation.tsx) so quotes and the
 * calculator stay in sync. Revenue and cost exclude the payment processing fee, which is passed
 * through to the customer and therefore doesn't affect profit. Gratuity is counted in both revenue
 * and cost (paid out to bartenders), so it doesn't affect profit either.
 */
export const calculateEstimatedTotals = (input: EstimatedTotalsInput): EstimatedTotals => {
    const {
        guests,
        hours,
        drinker,
        guestsPerBartender,
        bartenderPrepTime,
        addons = [],
        customSupplies = [],
        includeMandatoryPortableBar = true,
        includeMandatoryIceAndCooler = true,
        includeCustomSupplies = true,
        includePaymentProcessingFee = true,
        paymentProcessingFeeRate = 0.1,
        includeTips = false,
        tipPercentage = 0,
        includeBeerAndWine = false,
    } = input;
    const bartenderCost = Number(input.bartenderCost) || 0;
    const bartenderPrice = Number(input.bartenderPrice) || 0;
    const suppliesCost = Number(input.suppliesCost) || 0;
    const suppliesPrice = Number(input.suppliesPrice) || 0;
    const deliveryFee = Number(input.deliveryFee) || 0;
    const additionalProfit = Number(input.additionalProfit) || 0;
    const fixedExpenses =
        (Number(input.marketingExpense) || 0) +
        (Number(input.insuranceExpense) || 0) +
        (Number(input.otherExpense) || 0);
    const minPackagePrice = Number(input.minPackagePrice) || 0;

    const numberOfBartenders = getNumberOfBartenders(guests, guestsPerBartender);
    const calculatedDrinks = getEstimatedDrinks(guests, hours, drinker);
    const estimatedDrinks = includeBeerAndWine
        ? Math.round(calculatedDrinks * BEER_AND_WINE_COCKTAIL_RATIO)
        : calculatedDrinks;
    const bartenderHours = Number(hours) + Number(bartenderPrepTime) / 60;
    const addonArgs = [guests, numberOfBartenders, hours, bartenderPrepTime, drinker] as const;

    // Add-ons (cents -> dollars)
    const optionalAddons = addons.filter((addOn) => addOn.is_btc_edit);
    const portableBar = addons.filter((addOn) => !addOn.is_btc_edit && addOn.name == "Portable Bar");
    const iceAndCooler = addons.filter((addOn) => !addOn.is_btc_edit && addOn.name == "Ice and Coolers");
    const addOnTotal = getAddonsTotal(optionalAddons, ...addonArgs) / 100;
    const addonsTotalCost = getAddonsTotalCost(optionalAddons, ...addonArgs) / 100;
    const mandatoryPortableBarTotal = includeMandatoryPortableBar && portableBar.length
        ? getAddonsTotal([{ ...portableBar[0], auto_include: true }], ...addonArgs) / 100
        : 0;
    const mandatoryPortableBarCost = includeMandatoryPortableBar
        ? getAddonsTotalCost(portableBar, ...addonArgs) / 100
        : 0;
    const mandatoryIceAndCoolerTotal = includeMandatoryIceAndCooler && iceAndCooler.length
        ? getAddonsTotal([{ ...iceAndCooler[0], auto_include: true }], ...addonArgs) / 100
        : 0;
    const mandatoryIceAndCoolerCost = includeMandatoryIceAndCooler
        ? getAddonsTotalCost(iceAndCooler, ...addonArgs) / 100
        : 0;

    // Custom supplies (cents -> dollars)
    const customSupplyTotal = includeCustomSupplies
        ? getCustomSupplyTotal(customSupplies, guests, numberOfBartenders, hours) / 100
        : 0;
    const customSupplyTotalCost = includeCustomSupplies
        ? getCustomSupplyTotalCost(customSupplies, guests, numberOfBartenders, hours) / 100
        : 0;

    // Package (bartenders + cocktail supplies)
    const bartendingCost = numberOfBartenders * bartenderCost * bartenderHours;
    const cocktailCost = estimatedDrinks * suppliesCost;
    const packageTotalCost = bartendingCost + cocktailCost;
    const packageTotalPrice =
        numberOfBartenders * bartenderPrice * bartenderHours + estimatedDrinks * suppliesPrice;

    const mandatoryTotal = mandatoryPortableBarTotal + mandatoryIceAndCoolerTotal;
    const tipServiceTotal =
        packageTotalPrice + deliveryFee + additionalProfit + fixedExpenses +
        addOnTotal + customSupplyTotal + mandatoryTotal;
    const tipsTotal = includeTips ? (Number(tipPercentage) / 100) * tipServiceTotal : 0;

    const basePrice = packageTotalPrice + deliveryFee + tipsTotal + additionalProfit + fixedExpenses + customSupplyTotal;
    const revenueBeforeFee = Math.max(basePrice, minPackagePrice) + addOnTotal + mandatoryTotal;
    const paymentProcessingFee = includePaymentProcessingFee
        ? getPlatformServiceFee(revenueBeforeFee, paymentProcessingFeeRate)
        : 0;

    // Gratuity is collected from the customer but paid out to the bartenders, so it is an
    // expense rather than profit.
    const estimatedCost =
        packageTotalCost + deliveryFee + addonsTotalCost + mandatoryPortableBarCost +
        mandatoryIceAndCoolerCost + fixedExpenses + customSupplyTotalCost + tipsTotal;
    const estimatedTotalRevenue = revenueBeforeFee;
    const estimatedProfit = estimatedTotalRevenue - estimatedCost;

    const minPriceEffect =
        minPackagePrice > packageTotalPrice + deliveryFee + customSupplyTotal
            ? revenueBeforeFee + paymentProcessingFee -
            (packageTotalPrice + deliveryFee + addOnTotal + mandatoryTotal + additionalProfit +
                paymentProcessingFee + tipsTotal + fixedExpenses)
            : 0;

    return {
        estimatedTotalRevenue,
        estimatedCost,
        estimatedProfit,
        breakdown: {
            numberOfBartenders,
            estimatedDrinks,
            bartendingCost,
            cocktailCost,
            packageTotalCost,
            packageTotalPrice,
            addOnTotal,
            addonsTotalCost,
            mandatoryPortableBarTotal,
            mandatoryPortableBarCost,
            mandatoryIceAndCoolerTotal,
            mandatoryIceAndCoolerCost,
            customSupplyTotal,
            customSupplyTotalCost,
            paymentProcessingFee,
            tipsTotal,
            minPriceEffect,
        },
    };
};

/**
 * Convenience wrapper for quotes: builds the calculator inputs from a saved package
 * (values stored in cents) plus the event details, then runs calculateEstimatedTotals.
 */
export const calculateEstimatedTotalsFromPackage = (
    pkg: Package,
    event: EstimatedTotalsEvent,
    options: EstimatedTotalsOptions = {}
): EstimatedTotals =>
    calculateEstimatedTotals({
        guests: Number(event.guests) || 0,
        hours: Number(event.hours) || 0,
        drinker: event.drinker,
        guestsPerBartender: event.useGuestsPerBeer
            ? pkg.guestsPerBeer || 75
            : pkg.guestsPerBartender,
        bartenderPrepTime: pkg.prepMins,
        bartenderCost: (pkg.bartenderCost || 0) / 100,
        bartenderPrice: (pkg.bartenderPrice || 0) / 100,
        suppliesCost: (pkg.suppliesCost || 0) / 100,
        suppliesPrice: (pkg.suppliesPrice || 0) / 100,
        deliveryFee: (pkg.deliveryFee || 0) / 100,
        additionalProfit: (pkg.additionalProfit || 0) / 100,
        marketingExpense: (pkg.marketingExpense || 0) / 100,
        insuranceExpense: (pkg.insuranceExpense || 0) / 100,
        otherExpense: (pkg.otherExpense || 0) / 100,
        minPackagePrice: pkg.minPrice > 0 ? pkg.minPrice / 100 : 0,
        addons: (pkg.addons || []) as AddOnsCalInterface[],
        customSupplies: pkg.customSupply || [],
        tipPercentage: (pkg.tipsFee || 0) / 100,
        ...options,
    });

export const DynamicNumbers = (startTime: number, step = 1) => {
    // Generate an array of numbers from startTime to startTime + 11, at the given step

    const stepsCount = Math.round(11 / step) + 1;
    const numbers = Array.from(
        { length: stepsCount },
        (_, i) => Number((i * step + Number(startTime)).toFixed(1))
    );

    return (
        <div className="w-full md:flex hidden gap-1 justify-between ml-1">
            {numbers.map((number) => {
                if (
                    (number >= 10 && number < 100) ||
                    (number <= -10 && number > -100)
                ) {
                    return (
                        <span
                            key={number}
                            className="text-xs text-gray-500 dark:text-gray-400"
                        >
                            {number}
                        </span>
                    );
                } else {
                    return (
                        <span
                            key={number}
                            className="text-xs text-gray-500 dark:text-gray-400"
                        >
                            &nbsp;{number}
                        </span>
                    );
                }
            })}
        </div>
    );
};

export type GeoPoint = { lat: number; lng: number };

/**
 * Straight-line (great-circle) distance in miles. Matches getDistance() in the client app
 * (HOS_client_side shared utils), which is what bookings are priced with.
 */
export const getStraightLineMiles = (from: GeoPoint, to: GeoPoint) => {
    const EARTH_RADIUS_MILES = 3958.8;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(to.lat - from.lat);
    const dLng = toRad(to.lng - from.lng);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
};

/** Whole miles beyond the free distance (partial miles are billed as a full mile). */
export const getBillableMiles = (miles: number, freeMiles: number) =>
    Math.ceil(Math.max(miles - freeMiles, 0));

/**
 * Mileage charge in dollars. Mirrors getDeliveryTotal() in the client app: billable miles are
 * charged for the round trip (x2) and the result is rounded up to a whole dollar.
 */
export const getMileageCharge = (miles: number, costPerMile: number, freeMiles: number) => {
    const costPerMileCents = Math.round((Number(costPerMile) || 0) * 100);
    const chargeCents = costPerMileCents * getBillableMiles(miles, freeMiles) * 2;
    return Math.ceil(chargeCents / 100);
};
